'use client';

import { useId } from 'react';
import type { ReactNode, Ref } from 'react';
import { HintDisclosure } from './HintDisclosure';

interface CalculatorShellProps {
  title: string;
  /** A node, because some legal copy cites a source it has to link. */
  disclaimer: ReactNode;
  collapsibleDisclaimer?: boolean;
  /** Announced on change; keep result detail too long to hear out of it. */
  summary?: ReactNode;
  /** Focus target after a calculation. */
  summaryRef?: Ref<HTMLDivElement>;
  /** Scroll target after a calculation; the focus target is `sr-only` and has nothing to show. */
  resultsRef?: Ref<HTMLDivElement>;
  /**
   * Results shown beside the form rather than in place of it. Passing anything - including a falsy
   * placeholder - reserves the second column, so it does not appear on the first calculation.
   */
  results?: ReactNode;
  children: ReactNode;
}

export const CalculatorShell = ({
  title,
  disclaimer,
  collapsibleDisclaimer = false,
  summary,
  summaryRef,
  resultsRef,
  results,
  children,
}: CalculatorShellProps) => {
  const disclaimerId = `${useId()}-disclaimer`;

  return (
    <div className="font-sans [--btn-outline-hover:var(--color-kale)] [--btn-outline:var(--color-kale)]">
      {/* The page provides the h1. */}
      <div className="mb-6 flex items-center gap-2">
        <h2 className="text-heading-md">{title}</h2>
        {collapsibleDisclaimer && <HintDisclosure hintId={disclaimerId} label={title} hint={disclaimer} />}
      </div>

      <div role="status" ref={summaryRef} tabIndex={-1}>
        {summary}
      </div>

      {/* `md` is 1025px here; at `sm` the inner card is too narrow for a form and a chart side by side.
          The form takes 60% because an even split leaves its inputs cramped. */}
      {results === undefined ? (
        children
      ) : (
        <div className="grid gap-8 md:grid-cols-[6fr_4fr]">
          {children}
          {/* Omitted while empty: a stacked mobile grid would otherwise put a gap track under the form.
              `grid-template-columns` reserves the desktop column either way. */}
          {results && (
            /* Cleared by the sticky nav, which `scrollIntoView` would otherwise hide the panel top behind. */
            <div ref={resultsRef} className="scroll-mt-[var(--height-nav)]">
              {results}
            </div>
          )}
        </div>
      )}

      {!collapsibleDisclaimer && <p className="mt-8 text-legal">{disclaimer}</p>}
    </div>
  );
};
